import { del, get, post, put } from "../../helpers/utils/http-client";
import { APPS_ENDPOINT } from "../../consts/api-endpoints";

/**
 * Agent evaluation API.
 *
 * Two surfaces, matching botcircuits-backend/src/agent-eval:
 *   - plans : the test scenarios — create, read, save, delete
 *   - runs  : start a run, and read its results
 *
 * Starting a run returns immediately: a run is many multi-turn LLM
 * conversations and takes minutes. The page follows it by polling — see
 * `pollRun` below.
 */

const evalBase = (appId) => `${APPS_ENDPOINT}/${appId}/agent-eval`;

// ─── Areas ────────────────────────────────────────────────────────────────────
// Mirrors the runner's CATEGORY_ORDER: one area per kind of connected
// resource. The legacy ids keep older plans and runs readable.
export const CATEGORIES = [
	{ id: "instructions", name: "Instructions" },
	{ id: "knowledge_base", name: "Knowledge" },
	{ id: "workflow", name: "Workflows & Playbooks" },
	{ id: "skill", name: "Skills" },
	{ id: "tool_use", name: "Tools" },
	{ id: "mcp", name: "MCP Servers" },
	{ id: "sub_agent", name: "Sub-Agents" },
	{ id: "happy_path", name: "Happy Path" },
	{ id: "interruption", name: "Interruption" },
	{ id: "memory", name: "Memory" },
	{ id: "safety", name: "Safety" },
];

export const CATEGORY_NAMES = CATEGORIES.reduce((acc, c) => {
	acc[c.id] = c.name;
	return acc;
}, {});

export const STATUS = {
	PENDING: "pending",
	RUNNING: "running",
	PASSED: "passed",
	FAILED: "failed",
	ERROR: "error",
	SKIPPED: "skipped",
	CANCELLED: "cancelled",
};

// ─── Plans ────────────────────────────────────────────────────────────────────

export async function listPlans(appId) {
	try {
		const { response, status } = await get(`${evalBase(appId)}/plans`);
		if (status !== 200) {
			return [];
		}
		return response ?? [];
	} catch (err) {
		console.debug("listPlans failed cause", err.message, err.code);
		return [];
	}
}

export async function getPlan(appId, planId) {
	try {
		const { response, status } = await get(`${evalBase(appId)}/plans/${planId}`);
		if (status !== 200) {
			return null;
		}
		return response;
	} catch (err) {
		console.debug("getPlan failed cause", err.message, err.code);
		return null;
	}
}

/**
 * Create test scenarios for everything the agent is connected to.
 *
 * Returns a placeholder plan immediately (`generationStatus: generating`);
 * follow it with `pollPlan` while the runner reads the agent and writes the
 * scenarios. Pass a planId to recreate an existing plan in place.
 */
export async function generatePlan(appId, planId) {
	try {
		const path = planId ? `${evalBase(appId)}/plans/${planId}/generate` : `${evalBase(appId)}/plans/generate`;
		return await post(path, { background: true });
	} catch (err) {
		console.debug("generatePlan failed cause", err.message, err.code);
		return { response: err.displayError, status: err.code };
	}
}

export async function savePlan(appId, planId, plan) {
	try {
		if (planId) {
			return await put(`${evalBase(appId)}/plans/${planId}`, plan);
		}
		return await post(`${evalBase(appId)}/plans`, plan);
	} catch (err) {
		console.debug("savePlan failed cause", err.message, err.code);
		return { response: err.displayError, status: err.code };
	}
}

export async function deletePlan(appId, planId) {
	try {
		return await del(`${evalBase(appId)}/plans/${planId}`);
	} catch (err) {
		console.debug("deletePlan failed cause", err.message, err.code);
		return { response: err.displayError, status: err.code };
	}
}

// ─── Runs ─────────────────────────────────────────────────────────────────────

/**
 * Queue a run and return its persisted runId and initial progress immediately.
 *
 * @param options.planId     a stored plan to run
 * @param options.plan       or an inline plan (an imported file not yet saved)
 * @param options.categories run only these categories
 * @param options.caseIds    run only these cases
 */
export async function startRun(appId, options = {}) {
	try {
		return await post(`${evalBase(appId)}/runs`, {
			planId: options.planId,
			plan: options.plan,
			categories: options.categories,
			caseIds: options.caseIds,
			keepMemory: !!options.keepMemory,
		});
	} catch (err) {
		console.debug("startRun failed cause", err.message, err.code);
		return { response: err.displayError, status: err.code };
	}
}

export async function listRuns(appId, limit = 50) {
	try {
		const { response, status } = await get(`${evalBase(appId)}/runs`, { limit });
		if (status !== 200) {
			return [];
		}
		return response ?? [];
	} catch (err) {
		console.debug("listRuns failed cause", err.message, err.code);
		return [];
	}
}

/**
 * One run.
 *
 * `includeCases: false` fetches only the header — what a progress poll needs,
 * and far smaller than a run's full transcripts.
 */
export async function getRun(appId, runId, includeCases = true) {
	try {
		const { response, status } = await get(
			`${evalBase(appId)}/runs/${runId}`,
			includeCases ? {} : { includeCases: "false" }
		);
		if (status !== 200) {
			return null;
		}
		return response;
	} catch (err) {
		console.debug("getRun failed cause", err.message, err.code);
		return null;
	}
}

export async function deleteRun(appId, runId) {
	try {
		return await del(`${evalBase(appId)}/runs/${runId}`);
	} catch (err) {
		console.debug("deleteRun failed cause", err.message, err.code);
		return { response: err.displayError, status: err.code };
	}
}

// ─── Polling ──────────────────────────────────────────────────────────────────

const TERMINAL_STATUSES = [STATUS.PASSED, STATUS.FAILED, STATUS.ERROR, STATUS.CANCELLED, STATUS.SKIPPED];

export function isTerminal(status) {
	return TERMINAL_STATUSES.includes(status);
}

/**
 * Follow a run to completion, reporting progress as it goes.
 *
 * The runner rewrites the run header after every case, so each poll returns a
 * real summary rather than an estimate. Only the header is fetched while the
 * run is in flight; the full transcripts are loaded once at the end.
 *
 * @param appId - the app
 * @param runId - the run to follow
 * @param onProgress - called with each header while the run is in flight
 * @param options.intervalMs - how long between polls
 * @param options.timeoutMs - give up after this long
 * @returns {Promise<*>} the finished run, with cases
 */
export async function pollRun(appId, runId, onProgress, options = {}) {
	const intervalMs = options.intervalMs ?? 3000;
	const timeoutMs = options.timeoutMs ?? 45 * 60 * 1000;
	const deadline = Date.now() + timeoutMs;

	while (Date.now() < deadline && !options.signal?.aborted) {
		const header = await getRun(appId, runId, !!options.includeCases);
		if (options.signal?.aborted) return null;

		if (header) {
			if (onProgress) {
				onProgress(header);
			}
			if (isTerminal(header.status)) {
				// Fetch the final transcript unless this poll already included it.
				return options.includeCases ? header : await getRun(appId, runId, true);
			}
		}

		await new Promise(resolve => {
			const done = () => { clearTimeout(timer); options.signal?.removeEventListener('abort', done); resolve(); };
			const timer = setTimeout(done, intervalMs);
			options.signal?.addEventListener('abort', done, { once: true });
		});
	}
	if (options.signal?.aborted) return null;

	// Timed out watching. The run itself may still be going, so return whatever
	// it looks like now rather than reporting a failure that has not happened.
	return await getRun(appId, runId, true);
}

/** Follow scenario generation; a stopped watcher never means generation completed. */
export async function pollPlan(appId, planId, onProgress, { signal, intervalMs = 3000, timeoutMs = 15 * 60 * 1000 } = {}) {
    const deadline = Date.now() + timeoutMs;
    while (!signal?.aborted && Date.now() < deadline) {
        const plan = await getPlan(appId, planId);
        if (signal?.aborted) return null;
        if (!plan) throw new Error('Could not load scenario generation. Open Agent Tests to retry.');
        onProgress?.(plan);
        if (plan.generationStatus !== 'generating') return plan;
        await new Promise(resolve => {
            const done = () => { clearTimeout(timer); signal?.removeEventListener('abort', done); resolve(); };
            const timer = setTimeout(done, intervalMs);
            signal?.addEventListener('abort', done, { once: true });
        });
    }
    if (signal?.aborted) return null;
    throw new Error('Scenario generation is taking longer than expected. Open Agent Tests to check its status.');
}


// ─── Latest result per scenario ──────────────────────────────────────────────

const RESULT_RUNS = 5;

/**
 * The newest result of every scenario in a plan, across its recent runs.
 *
 * Running one scenario again after a fix makes a run of one; the page should
 * still show every other scenario's last result, not "not run". So results are
 * merged newest-first over the plan's last few runs.
 *
 * @returns {Promise<Object>} caseId → case result
 */
export async function latestResults(appId, planId, runHeaders) {
	const results = {};
	const runs = (runHeaders || []).filter(run => run.planId === planId).slice(0, RESULT_RUNS);
	for (const header of runs) {
		const run = await getRun(appId, header.id, true);
		for (const result of run?.cases || []) {
			if (!results[result.caseId] && result.status !== STATUS.CANCELLED) {
				results[result.caseId] = { ...result, runId: header.id, ranAt: header.startedAt };
			}
		}
	}
	return results;
}

/** Counts for the summary, over the scenarios of the current plan only. */
export function summarize(scenarios, results) {
	const counts = { total: scenarios.length, passed: 0, failed: 0, errored: 0, notRun: 0 };
	for (const scenario of scenarios) {
		const status = results[scenario.id]?.status;
		if (status === STATUS.PASSED) counts.passed += 1;
		else if (status === STATUS.FAILED) counts.failed += 1;
		else if (status === STATUS.ERROR) counts.errored += 1;
		else counts.notRun += 1;
	}
	const judged = counts.passed + counts.failed;
	counts.score = judged ? Math.round((counts.passed / judged) * 100) : null;
	return counts;
}
